import axios from "axios";

const BASE_API = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

// Get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('eez7_client_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Get ticket types for an event (public; sends auth header when available
 * so the backend can authorize private/invitation-only events for invited users)
 * @param {number} eventId - The event ID
 * @returns {Promise<Array>} - Array of ticket types
 */
export const getEventTicketTypes = async (eventId) => {
  try {
    const response = await axios.get(
      `${BASE_API}/events/${eventId}/ticket-types`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching ticket types:", error);
    return [];
  }
};

/**
 * Reserve tickets for an event (public; sends auth header when available
 * so the backend can authorize private events for invited users)
 * @param {number} eventId - The event ID
 * @param {Object} reservationData - Reservation data including buyer info and tickets
 * @returns {Promise<Object>} - Created reservation/order
 */
export const reserveEventTickets = async (eventId, reservationData) => {
  const response = await axios.post(
    `${BASE_API}/events/${eventId}/reserve`,
    reservationData,
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Validate a promo code for an event + ticket selection (public).
 * Returns the discount breakdown so the UI can preview the savings before checkout.
 */
export const validatePromoCodePublic = async ({ code, eventId, ticketTypeIds, buyerEmail }) => {
  const response = await axios.post(
    `${BASE_API}/public/promo-codes/validate`,
    { code, eventId, ticketTypeIds, buyerEmail }
  );
  return response.data;
};

/**
 * Validate a promo code for a REGULAR reservation (no event).
 * Scoped by restaurant + booking date, so a code locked to one day is rejected
 * on any other date.
 */
export const validateReservationPromoCodePublic = async ({
  code, restaurantId, date, pricePerGuestCents, guests, buyerEmail,
}) => {
  const response = await axios.post(
    `${BASE_API}/public/promo-codes/validate-reservation`,
    { code, restaurantId, date, pricePerGuestCents, guests, buyerEmail }
  );
  return response.data;
};

/**
 * Get user's ticket orders (authenticated)
 * @returns {Promise<Object>} - User's ticket orders
 */
export const getMyTicketOrders = async () => {
  const response = await axios.get(
    `${BASE_API}/events/my-orders`,
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Get order details (authenticated)
 * @param {number} id - The order ID
 * @returns {Promise<Object>} - Order details with tickets
 */
export const getOrderDetails = async (id) => {
  const response = await axios.get(
    `${BASE_API}/events/orders/${id}`,
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Get order details by payment token (public - for payment page)
 * @param {string} paymentToken - The payment token
 * @returns {Promise<Object>} - Order details
 */
export const getOrderByPaymentToken = async (paymentToken) => {
  const response = await axios.get(
    `${BASE_API}/events/orders/payment/${paymentToken}`
  );
  return response.data;
};

/**
 * Check if an event has ticket types available
 * @param {number} eventId - The event ID
 * @returns {Promise<boolean>} - Whether tickets are available
 */
export const hasTicketsAvailable = async (eventId) => {
  try {
    const response = await getEventTicketTypes(eventId);
    // Handle both array and { data: [...] } response formats
    const types = Array.isArray(response) ? response : (response.data || []);
    return types.length > 0 && types.some(t =>
      t.remainingQuantity == null || t.remainingQuantity > 0
    );
  } catch {
    return false;
  }
};

/**
 * Calculate order total
 * @param {Array} selectedTickets - Array of selected tickets with quantity and price
 * @returns {Object} - Subtotal, VAT, and total
 */
export const calculateOrderTotal = (selectedTickets) => {
  let subtotal = 0;
  let vatAmount = 0;

  selectedTickets.forEach(ticket => {
    const basePrice = (ticket.priceCents / 100) * ticket.quantity;
    if (ticket.priceExcludesVat) {
      subtotal += basePrice;
      vatAmount += basePrice * 0.14;
    } else {
      subtotal += basePrice;
    }
  });

  return {
    subtotal,
    vatAmount,
    total: subtotal + vatAmount
  };
};
